import { FormEvent, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Lock, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import * as userApi from '@/lib/userApi'
import { MicrosoftButton } from './MicrosoftButton'
import { GoogleButton } from './GoogleButton'

// Auth configuration from environment variables
const SHOW_SOCIAL_LOGIN = import.meta.env.VITE_SHOW_SOCIAL_LOGIN === 'true'
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || 'local_only'

interface SimpleAuthFormProps {
  onSuccess?: () => void
  submitLabel?: string
  autoFocusEmail?: boolean
  className?: string
  footerSlot?: ReactNode
  /** Override to show/hide social buttons (defaults to VITE_SHOW_SOCIAL_LOGIN) */
  showSocialButtons?: boolean
}

export function SimpleAuthForm({
  onSuccess,
  submitLabel = 'Iniciar sesión',
  autoFocusEmail = true,
  className,
  footerSlot,
  showSocialButtons = SHOW_SOCIAL_LOGIN,
}: SimpleAuthFormProps) {
  // Determine if we should show the local login form
  // In 'ciam_only' mode, hide the form and show only social buttons
  const showLocalForm = AUTH_MODE !== 'ciam_only'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Listen for OAuth popup errors (e.g. USER_NOT_REGISTERED)
  useEffect(() => {
    function handleOAuthError(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'oauth_error' && event.data?.error) {
        setError(event.data.error)
      }
    }
    window.addEventListener('message', handleOAuthError)
    return () => window.removeEventListener('message', handleOAuthError)
  }, [])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setHasSubmitted(true)
    setError(null)

    const normalizedEmail = email.trim();

    if (!normalizedEmail || !password) {
      return
    }

    setIsLoading(true)
    try {
      await userApi.login({ usernameOrEmail: normalizedEmail, password })
      setEmail('')
      setPassword('')
      setHasSubmitted(false)
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión')
    } finally {
      setIsLoading(false)
    }
  }

  const showRequiredError = hasSubmitted && (!email.trim() || !password.trim())
  const emailError = showRequiredError && !email.trim()
  const passwordError = showRequiredError && !password.trim()

  return (
    <form className={className ?? 'space-y-6'} onSubmit={handleSubmit}>
      {/* Local Login Form - shown unless AUTH_MODE is 'ciam_only' */}
      {showLocalForm && (
      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="employee-email">Correo electrónico</Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Mail className="h-4 w-4" aria-hidden="true" />
            </div>
            <Input
              id="employee-email"
              type="email"
              autoComplete="email"
              placeholder="tu.email@xgoldit.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoFocus={autoFocusEmail}
              aria-invalid={emailError ? 'true' : undefined}
              className="pl-10"
            />
          </div>
          <p className="text-xs text-muted-foreground">Utiliza tu correo corporativo @xgoldit.com</p>
          {emailError && (
            <p className="text-xs text-destructive">Ingresa tu correo electrónico para continuar.</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="employee-password">Contraseña</Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Lock className="h-4 w-4" aria-hidden="true" />
            </div>
            <Input
              id="employee-password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              aria-invalid={passwordError ? 'true' : undefined}
              className="pl-10"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Mínimo 8 caracteres
            </p>
            <Link
              to="/forgot-password"
              className="text-xs text-primary hover:underline"
              tabIndex={-1}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          {passwordError && (
            <p className="text-xs text-destructive">Ingresa tu contraseña para continuar.</p>
          )}
        </div>
      </div>
      )}

      {!showRequiredError && error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <ShieldAlert className="mt-0.5 h-4 w-4" aria-hidden="true" />
          <p role="alert">{error}</p>
        </div>
      )}

      {showLocalForm && (
        <Button type="submit" className="h-11 w-full" disabled={isLoading}>
          {isLoading ? 'Verificando…' : submitLabel}
        </Button>
      )}

      {/* Social Login Buttons - PROJ-025 */}
      {showSocialButtons && (
        <div className="space-y-3">
          {showLocalForm && (
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  o continúa con
                </span>
              </div>
            </div>
          )}
          <MicrosoftButton disabled={isLoading} />
          <GoogleButton disabled={isLoading} />
        </div>
      )}

      {footerSlot}
    </form>
  )
}
